import { authenticatedFetch, clearCachedInitData } from './auth'
import { clearCachedUserInfo } from './mobilePaymentBarApi'

export interface CouponRewardPresentation { label: string; details?: string; locale?: string }
export interface CouponReward { type: string; amount: number; subType?: string; extra?: number; presentation?: CouponRewardPresentation }
export interface CouponResult { items: CouponReward[]; balance?: number }
const errors: Record<string, string> = {
    used: 'Dieser Gutscheincode wurde bereits eingelöst.',
    userHasRedeemed: 'Du hast diesen Gutscheincode bereits eingelöst.',
    'user limit reached': 'Du hast das Einlöselimit für diesen Gutscheincode erreicht.',
    'limit-reached': 'Das Einlöselimit für diesen Gutscheincode wurde erreicht.',
    'coupon-limit-reached': 'Das Einlöselimit für diesen Gutscheincode wurde erreicht.',
    invalid: 'Dieser Gutscheincode ist ungültig oder nicht mehr verfügbar.',
}
export async function redeemCoupon(code: string): Promise<CouponResult> {
    const trimmed = code.trim()
    if (!trimmed) throw new Error('Bitte gib einen Gutscheincode ein.')
    let response: Response
    try {
        response = await authenticatedFetch('/api/vouchers/codeRedeem', { method: 'POST', body: JSON.stringify({ code: trimmed }) })
    } catch {
        throw new Error('Der Einlösestatus konnte nicht bestätigt werden. Prüfe dein Guthaben oder versuche es später erneut.')
    }
    const result = await response.json().catch(() => null)
    if (!response.ok || !result || result.r === false) throw new Error('Der Gutscheincode konnte nicht eingelöst werden. Bitte versuche es später erneut.')
    if (result.result !== 'redeemed') throw new Error(errors[result.result] || errors.invalid)
    clearCachedInitData()
    clearCachedUserInfo()
    // Older web coupons return only an amount; the web client treats that as chips.
    const numeric = (value: unknown): number | undefined => {
        if (typeof value !== 'number' && (typeof value !== 'string' || !value.trim())) return undefined
        const number = Number(value)
        return Number.isFinite(number) && number >= 0 ? number : undefined
    }
    const items: CouponReward[] = Array.isArray(result.items) ? result.items.flatMap((item: unknown) => {
        if (!item || typeof item !== 'object' || !('type' in item) || typeof item.type !== 'string') return []
        const raw = item as Record<string, unknown>
        const amount = numeric(raw.amount)
        if (amount === undefined) return []
        const display = raw.presentation as Record<string, unknown> | null | undefined
        const presentation = display && typeof display.label === 'string' && display.label.trim() ? {
            label: display.label,
            details: typeof display.details === 'string' ? display.details : undefined,
            locale: typeof display.locale === 'string' ? display.locale : undefined,
        } : undefined
        return [{ type: item.type, amount, presentation, subType: typeof raw.subType === 'string' ? raw.subType : undefined, extra: numeric(raw.extra) }]
    }) : numeric(result.amount) !== undefined ? [{ type: 'chips', amount: numeric(result.amount)! }] : []
    return {
        items,
        balance: typeof result.balance === 'number' && Number.isFinite(result.balance) ? result.balance : undefined,
    }
}
